const pickField = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

const toDateOnly = (value) => {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

/** Mirrors backend RequestFollowUpServices.GetFollowUpReminder */
export const computeFollowUpReminder = (nextFollowUpDate, followUpCount = 0) => {
  if (followUpCount <= 0) {
    return {
      followUpReminderStatus: "none",
      followUpReminderTitle: "بدون پیگیری",
      followUpReminderColor: "gray",
      alertSeverity: "info",
    };
  }

  const nextDate = toDateOnly(nextFollowUpDate);
  if (!nextDate) {
    return {
      followUpReminderStatus: null,
      followUpReminderTitle: null,
      followUpReminderColor: null,
      alertSeverity: "info",
    };
  }

  const today = toDateOnly(new Date());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (nextDate < today) {
    return {
      followUpReminderStatus: "overdue",
      followUpReminderTitle: "پیگیری عقب‌افتاده",
      followUpReminderColor: "error",
      alertSeverity: "error",
    };
  }

  if (nextDate <= tomorrow) {
    return {
      followUpReminderStatus: "today",
      followUpReminderTitle: "پیگیری امروز",
      followUpReminderColor: "warning",
      alertSeverity: "warning",
    };
  }

  return {
    followUpReminderStatus: "future",
    followUpReminderTitle: "پیگیری آینده",
    followUpReminderColor: "success",
    alertSeverity: "info",
  };
};

/** Mirrors backend GetSummaryByRequestIds ordering for next follow-up date. */
export const summarizeFollowUps = (followUps = []) => {
  const list = (followUps || []).filter(Boolean);
  if (!list.length) {
    return {
      followUpCount: 0,
      lastFollowUpDate: null,
      nextFollowUpDate: null,
      ...computeFollowUpReminder(null, 0),
    };
  }

  const ordered = [...list].sort((a, b) => {
    const dateA = new Date(pickField(a, "createOn", "CreateOn") || 0).getTime();
    const dateB = new Date(pickField(b, "createOn", "CreateOn") || 0).getTime();
    if (dateB !== dateA) return dateB - dateA;
    return (pickField(b, "id", "Id") || 0) - (pickField(a, "id", "Id") || 0);
  });

  const latest = ordered[0];
  const withNext = ordered.find((item) =>
    pickField(item, "nextFollowUpDate", "NextFollowUpDate")
  );
  const nextFollowUpDate = pickField(withNext, "nextFollowUpDate", "NextFollowUpDate");
  const reminder = computeFollowUpReminder(nextFollowUpDate, list.length);

  return {
    followUpCount: list.length,
    lastFollowUpDate: pickField(latest, "createOn", "CreateOn"),
    nextFollowUpDate,
    ...reminder,
  };
};

export const summarizeFollowUpFromRow = (row = {}) => {
  const followUpCount = Number(row.followUpCount ?? row.FollowUpCount ?? 0);
  const nextFollowUpDate = pickField(row, "nextFollowUpDate", "NextFollowUpDate");
  const status = pickField(row, "followUpReminderStatus", "FollowUpReminderStatus");
  const title = pickField(row, "followUpReminderTitle", "FollowUpReminderTitle");
  const color = pickField(row, "followUpReminderColor", "FollowUpReminderColor");

  if (status || title) {
    const alertSeverity =
      status === "overdue" ? "error" : status === "today" ? "warning" : "info";
    return {
      followUpCount,
      nextFollowUpDate,
      followUpReminderStatus: status,
      followUpReminderTitle: title,
      followUpReminderColor: color,
      alertSeverity,
    };
  }

  return {
    followUpCount,
    nextFollowUpDate,
    ...computeFollowUpReminder(nextFollowUpDate, followUpCount),
  };
};

export const matchesFollowUpStatusFilter = (row, filter) => {
  if (!filter) return true;
  const hasNoFollowUp = Number(row.followUpCount || 0) === 0;
  if (filter === "overdue") return row.followUpReminderStatus === "overdue";
  if (filter === "today") return row.followUpReminderStatus === "today";
  if (filter === "none") return hasNoFollowUp;
  return true;
};

export const getFollowUpRowHighlight = (row) => {
  if (row?.followUpReminderStatus === "overdue") return "error";
  if (row?.followUpReminderStatus === "today") return "warning";
  return "";
};
